// P05-5-4: Suggestions Panel Component
// Displays personalized coach and challenge suggestions

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SuggestionCard } from './SuggestionCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles } from 'lucide-react';

interface CoachSuggestion {
  type: 'coach';
  user_id: string;
  full_name: string;
  coach_bio: string | null;
  follower_count: number;
}

interface ChallengeSuggestion {
  type: 'challenge';
  id: string;
  title: string;
  description: string | null;
  coach_id: string;
  coach_name: string;
  end_at: string;
}

type Suggestion = CoachSuggestion | ChallengeSuggestion;

interface SuggestionsPanelProps {
  userId: string;
  region?: string;
  zipCode?: string;
  radius?: number;
  country?: 'US' | 'CA';
  skillLevel?: string;
}

export function SuggestionsPanel({ userId, region, zipCode, radius = 25, country, skillLevel }: SuggestionsPanelProps) {
  // Fetch coaches
  const { data: coaches, isLoading: coachesLoading } = useQuery({
    queryKey: ['suggestions', 'coaches', userId, country, zipCode, radius, region],
    queryFn: async (): Promise<CoachSuggestion[]> => {
      // US users with ZIP code: use radius search
      if (country === 'US' && zipCode) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('Not authenticated');

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lookup-coach-radius`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ zip: zipCode, radius }),
            }
          );

          if (response.ok) {
            const result = await response.json();
            return (result.coaches || []).slice(0, 6).map((c: any) => ({
              type: 'coach' as const,
              user_id: c.user_id,
              full_name: c.full_name,
              coach_bio: c.coach_bio,
              follower_count: c.follower_count || 0,
            }));
          }
          // Fallback to region-based search on error
          console.warn('[SuggestionsPanel] ZIP radius search failed, falling back to region');
        } catch (error) {
          console.error('[SuggestionsPanel] ZIP radius search error:', error);
          // Fallback to region-based search
        }
      }

      // Canada or fallback: use region-based filtering
      let query = supabase
        .from('coach_directory_view')
        .select('user_id, full_name, coach_bio, follower_count, region')
        .limit(6);

      // Filter by region (state/province) if available
      if (region) {
        query = query.eq('region', region);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []).map(coach => ({
        type: 'coach' as const,
        ...coach,
        follower_count: coach.follower_count || 0,
      }));
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });

  // Fetch active challenges
  const { data: challenges, isLoading: challengesLoading } = useQuery({
    queryKey: ['suggestions', 'challenges', userId],
    queryFn: async (): Promise<ChallengeSuggestion[]> => {
      const now = new Date().toISOString();
      
      // TODO: Re-enable when coach_challenges table is available
      // Get active challenges (start_at <= now AND end_at >= now)
      // const { data: challengeData, error: challengeError } = await supabase
      //   .from('coach_challenges')
      //   .select(`
      //     id,
      //     title,
      //     description,
      //     coach_id,
      //     end_at
      //   `)
      //   .lte('start_at', now)
      //   .gte('end_at', now)
      //   .limit(6);

      // if (challengeError) throw challengeError;
      const challengeData = null; // Temporary fallback

      // Get coach names
      const coachIds = [...new Set(challengeData?.map(c => c.coach_id) || [])];
      const { data: coachProfiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', coachIds);

      const coachMap = new Map(coachProfiles?.map(c => [c.user_id, c.full_name]) || []);

      return (challengeData || []).map(challenge => ({
        type: 'challenge' as const,
        id: challenge.id,
        title: challenge.title,
        description: challenge.description,
        coach_id: challenge.coach_id,
        coach_name: coachMap.get(challenge.coach_id) || 'Coach',
        end_at: challenge.end_at,
      })).filter(challenge => challenge.coach_name !== 'Coach' || challenge.coach_id); // Filter out invalid challenges
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });

  const isLoading = coachesLoading || challengesLoading;
  const hasSuggestions = (coaches && coaches.length > 0) || (challenges && challenges.length > 0);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#3DC68D]" />
            Suggestions for You
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasSuggestions) {
    return null; // Don't show panel if no suggestions
  }

  const allSuggestions: Suggestion[] = [
    ...(coaches || []),
    ...(challenges || []),
  ].slice(0, 6); // Limit to 6 total suggestions

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#3DC68D]" />
          Suggestions for You
        </CardTitle>
        <CardDescription>
          Coaches and challenges we think you'll like
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allSuggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.type === 'coach' ? suggestion.user_id : suggestion.id}
              suggestion={suggestion}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

